// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { emptyGuid } from "@insite/client-framework/Common/StringHelpers";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import {
    getCategoriesDataView,
    getCategoryState,
    getSubCategoryIds,
} from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import loadCategories from "@insite/client-framework/Store/Data/Categories/Handlers/LoadCategories";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps, GridWidths } from "@insite/mobius/GridItem";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { SharedProductPagesContexts } from "@werner/XDL/constants";
import { color } from "@werner/XDL/Utilities";
import * as React from "react";
import { useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css, ThemeProps, withTheme } from "styled-components";

const enum fields {
    title = "title",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
    };
}

const mapStateToProps = (state: ApplicationState, props: OwnProps) => {
    const getCategoriesParameter = {
        maxDepth: 1,
        includeStartCategory: false,
    };
    return {
        categoriesDataView: getCategoriesDataView(state, getCategoriesParameter),
        getCategoriesParameter,
        topLevelCategoryIds: getSubCategoryIds(state, emptyGuid),
    };
};

const mapDispatchToProps = {
    loadCategories,
};

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    ThemeProps<BaseTheme>;

export interface CategoryListNavigationStyles {
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    container?: InjectableCss;
    title?: TypographyProps;
    categoriesWrapper?: InjectableCss;
    categoryLink?: LinkPresentationProps;
}

export const categoryListNavigationStyles: CategoryListNavigationStyles = {
    centeringWrapper: {
        css: css`
            height: 300px;
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        css: css`
            margin: auto;
        `,
    },

    container: {
        css: css`
            background: ${color("common.white")};
            display: flex;
            flex-direction: column;
            row-gap: 22px;
            padding-top: 10px;
            margin-bottom: 30px;
            border-top: 1px solid ${color("common.border")};
        `,
    },
    title: {
        size: 22,
        weight: 500,
        css: css`
            letter-spacing: 0;
            line-height: 28px;
        `,
    },
    categoriesWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            row-gap: 18px;
        `,
    },
    categoryLink: {
        color: "secondary",
        typographyProps: {
            size: 16,
            weight: 400,
            css: css`
                max-width: 100%;
                text-overflow: ellipses;
                overflow: hidden;
                letter-spacing: 0;
                line-height: 22px;
            `,
        },
    },
};

const styles = categoryListNavigationStyles;

const CategoryListNavigation = ({
    categoriesDataView,
    loadCategories,
    getCategoriesParameter,
    topLevelCategoryIds,
    fields,
}: Props) => {
    useEffect(() => {
        if (!categoriesDataView.isLoading && !categoriesDataView.value) {
            loadCategories(getCategoriesParameter);
        }
    });

    if (categoriesDataView.isLoading) {
        return (
            <StyledWrapper {...styles.centeringWrapper}>
                <LoadingSpinner {...styles.spinner} />
            </StyledWrapper>
        );
    }

    if (!categoriesDataView.value || !topLevelCategoryIds) {
        return null;
    }

    const categories = categoriesDataView.value?.filter(o => topLevelCategoryIds.indexOf(o.id) > -1);
    return (
        <StyledWrapper {...styles.container}>
            <Typography {...styles.title}>{fields.title}</Typography>
            <StyledWrapper {...styles.categoriesWrapper}>
                {categories.map(category => (
                    <Link
                        key={category.id}
                        href={category.path}
                        {...styles.categoryLink}
                        aria-label={`${category.shortDescription ?? category.name} Category Link`}
                    >
                        {category.shortDescription ?? category.name}
                    </Link>
                ))}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(CategoryListNavigation),
    definition: {
        group: "Xngage Custom" as any,
        icon: "list-ul",
        allowedContexts: [...SharedProductPagesContexts] as any,
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                sortOrder: 1,
            },
        ],
    },
};

export default widgetModule;
